import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  FaArrowLeft,
  FaArrowRight,
  FaCalendarAlt,
  FaCheckCircle,
  FaCloudSun,
  FaExclamationTriangle,
  FaLeaf,
  FaMapMarkerAlt,
  FaMicrochip,
  FaSatellite,
  FaSeedling,
  FaTint,
} from 'react-icons/fa';
import { ThemeContext } from '../App';
import '../styles/module-shell.css';
import './PropriedadeOperacionalPreview.css';

const STATUS = {
  critical: { label: 'Atenção imediata', icon: FaExclamationTriangle },
  watch: { label: 'Acompanhar hoje', icon: FaCloudSun },
  stable: { label: 'Condição adequada', icon: FaCheckCircle },
};

function formatArea(value) {
  return Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 });
}

function ModuleShortcut({ icon: Icon, label, detail, onClick, tone }) {
  return (
    <button type="button" className={`prop-op-shortcut tone-${tone}`} onClick={onClick}>
      <span><Icon aria-hidden="true" /></span>
      <div><strong>{label}</strong><small>{detail}</small></div>
      <FaArrowRight aria-hidden="true" />
    </button>
  );
}

function OperationalHeader({ eyebrow, title, subtitle, onBack }) {
  return (
    <header className="module-topbar prop-op-topbar">
      <button type="button" className="prop-op-back" onClick={onBack} aria-label="Voltar">
        <FaArrowLeft aria-hidden="true" /><span>Voltar</span>
      </button>
      <span className="prop-op-topbar-divider" aria-hidden="true" />
      <div className="prop-op-topbar-icon" aria-hidden="true">🌾</div>
      <div className="prop-op-topbar-copy">
        <small>{eyebrow}</small>
        <strong>{title}</strong>
        <span>{subtitle}</span>
      </div>
      <span className="prop-op-demo-chip">Dados demonstrativos</span>
    </header>
  );
}

function TalhaoOperationalDetail({ propriedade, talhao, darkMode, onBack, onNavigate }) {
  const status = STATUS[talhao.operationalStatus] || STATUS.stable;
  const StatusIcon = status.icon;
  const timeline = [
    { icon: FaSeedling, title: 'Plantio registrado', detail: talhao.data_plantio ? new Date(`${talhao.data_plantio}T12:00:00`).toLocaleDateString('pt-BR') : 'Data não informada' },
    { icon: FaMicrochip, title: 'Última leitura do campo', detail: `${talhao.lastReading || 'Sem leitura recente'} · ${talhao.sensorName || 'Sensor não vinculado'}` },
    { icon: FaSatellite, title: 'Imagem de satélite analisada', detail: `${talhao.imageDate || 'Sem imagem'} · NDVI ${Number(talhao.ndvi || 0).toFixed(2)}` },
    { icon: FaCalendarAlt, title: 'Próxima ação recomendada', detail: talhao.nextAction || 'Continuar acompanhamento' },
  ];

  return (
    <div className={`module-shell prop-op-shell ${darkMode ? 'is-dark' : ''}`}>
      <OperationalHeader
        eyebrow="Talhão em acompanhamento"
        title={talhao.nome}
        subtitle={`${propriedade.nome} · ${talhao.cultura} · ${formatArea(talhao.area)} ha`}
        onBack={onBack}
      />

      <main className="prop-op-content">
        <section className={`prop-op-detail-hero status-${talhao.operationalStatus}`}>
          <div>
            <span className="prop-op-eyebrow">Decisão do dia</span>
            <h1>{talhao.nextAction}</h1>
            <p>{talhao.recommendationDetail}</p>
            <span className="prop-op-status"><StatusIcon aria-hidden="true" /> {status.label}</span>
          </div>
          <button type="button" onClick={() => onNavigate('irrigacao')}>
            Abrir manejo de irrigação <FaArrowRight aria-hidden="true" />
          </button>
        </section>

        <section className="prop-op-metrics" aria-label="Indicadores do talhão">
          <article><span>Água no solo</span><strong>{talhao.waterPct}%</strong><small>{talhao.waterPct < 40 ? 'Abaixo do ideal' : 'Faixa monitorada'}</small></article>
          <article><span>NDVI médio</span><strong>{Number(talhao.ndvi).toFixed(2)}</strong><small>{talhao.ndviChange > 0 ? `+${talhao.ndviChange}% na última imagem` : `${talhao.ndviChange}% na última imagem`}</small></article>
          <article><span>Fase atual</span><strong>{talhao.stage}</strong><small>{talhao.age}</small></article>
          <article><span>Última leitura</span><strong>{talhao.lastReading}</strong><small>{talhao.sensorName}</small></article>
        </section>

        <section className="prop-op-detail-grid">
          <article className="prop-op-panel prop-op-timeline">
            <div className="prop-op-section-heading">
              <div><span>Histórico integrado</span><h2>Linha do tempo do talhão</h2></div>
            </div>
            <ol>
              {timeline.map(({ icon: Icon, title, detail }) => (
                <li key={title}>
                  <span><Icon aria-hidden="true" /></span>
                  <div><strong>{title}</strong><p>{detail}</p></div>
                </li>
              ))}
            </ol>
          </article>

          <aside className="prop-op-panel prop-op-connections">
            <div className="prop-op-section-heading">
              <div><span>Fontes conectadas</span><h2>Abrir análise completa</h2></div>
            </div>
            <ModuleShortcut icon={FaMicrochip} label="Sensores" detail={talhao.sensorName} tone="blue" onClick={() => onNavigate('sensores')} />
            <ModuleShortcut icon={FaTint} label="Irrigação" detail={`${talhao.waterPct}% de água disponível`} tone="aqua" onClick={() => onNavigate('irrigacao')} />
            <ModuleShortcut icon={FaSatellite} label="Central de Operações" detail={`NDVI ${Number(talhao.ndvi).toFixed(2)} · ${talhao.imageDate}`} tone="purple" onClick={() => onNavigate('sensoriamento')} />
          </aside>
        </section>
      </main>
    </div>
  );
}

export default function PropriedadeOperacionalPreview({ propriedade, talhoes, onBack, onNavigate, initialPlotId = null, initialPlotName = '' }) {
  const { darkMode } = useContext(ThemeContext);
  const [talhaoSelecionado, setTalhaoSelecionado] = useState(null);
  const summary = useMemo(() => {
    const usedArea = talhoes.reduce((total, item) => total + Number(item.area || 0), 0);
    const critical = talhoes.filter(item => item.operationalStatus === 'critical');
    const connected = talhoes.filter(item => item.sensorName).length;
    return { usedArea, critical, connected };
  }, [talhoes]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [talhaoSelecionado]);

  useEffect(() => {
    if (!initialPlotId && !initialPlotName) return;
    const talhaoAlvo = talhoes.find(item => (
      (initialPlotId && String(item.id) === String(initialPlotId))
      || (initialPlotName && item.nome === initialPlotName)
    ));
    if (talhaoAlvo) setTalhaoSelecionado(talhaoAlvo);
  }, [initialPlotId, initialPlotName, talhoes]);

  if (talhaoSelecionado) {
    return (
      <TalhaoOperationalDetail
        propriedade={propriedade}
        talhao={talhaoSelecionado}
        darkMode={darkMode}
        onBack={() => setTalhaoSelecionado(null)}
        onNavigate={onNavigate}
      />
    );
  }

  const priorityTalhao = summary.critical[0];

  return (
    <div className={`module-shell prop-op-shell ${darkMode ? 'is-dark' : ''}`}>
      <OperationalHeader
        eyebrow="Visão operacional"
        title={propriedade.nome}
        subtitle={`${propriedade.municipio}/${propriedade.estado} · ${formatArea(propriedade.area)} ha`}
        onBack={onBack}
      />

      <main className="prop-op-content">
        <section className="prop-op-property-hero">
          <div className="prop-op-property-copy">
            <span className="prop-op-eyebrow">Resumo da propriedade</span>
            <h1>Operação concentrada em um só lugar</h1>
            <p>Acompanhe área plantada, sensores, necessidade hídrica e vigor vegetativo antes de decidir no campo.</p>
            <div className="prop-op-location"><FaMapMarkerAlt aria-hidden="true" /> {propriedade.municipio}, {propriedade.estado}</div>
          </div>
          <div className="prop-op-property-stats">
            <article><span>Área acompanhada</span><strong>{formatArea(summary.usedArea)} ha</strong><small>de {formatArea(propriedade.area)} ha</small></article>
            <article><span>Talhões ativos</span><strong>{talhoes.length}</strong><small>{summary.connected} com sensores</small></article>
            <article><span>Atenções abertas</span><strong>{summary.critical.length}</strong><small>{summary.critical.length ? 'prioridade hoje' : 'operação estável'}</small></article>
          </div>
        </section>

        {priorityTalhao && (
          <section className="prop-op-priority" aria-label="Prioridade operacional">
            <span><FaExclamationTriangle aria-hidden="true" /></span>
            <div>
              <small>Prioridade operacional</small>
              <strong>{priorityTalhao.nome}: {priorityTalhao.nextAction}</strong>
              <p>{priorityTalhao.recommendationDetail}</p>
            </div>
            <button type="button" onClick={() => setTalhaoSelecionado(priorityTalhao)}>Analisar talhão <FaArrowRight aria-hidden="true" /></button>
          </section>
        )}

        <section className="prop-op-shortcuts-section">
          <div className="prop-op-section-heading">
            <div><span>Módulos conectados</span><h2>Continue a análise</h2></div>
          </div>
          <div className="prop-op-shortcuts-grid">
            <ModuleShortcut icon={FaMicrochip} label="Sensores" detail="3 dispositivos com leituras" tone="blue" onClick={() => onNavigate('sensores')} />
            <ModuleShortcut icon={FaTint} label="Irrigação" detail="2 talhões pedem manejo" tone="aqua" onClick={() => onNavigate('irrigacao')} />
            <ModuleShortcut icon={FaSatellite} label="Central de Operações" detail="Imagem de 09 ago disponível" tone="purple" onClick={() => onNavigate('sensoriamento')} />
          </div>
        </section>

        <section className="prop-op-plots-section">
          <div className="prop-op-section-heading">
            <div><span>Acompanhamento por área</span><h2>Talhões da propriedade</h2></div>
            <small>Selecione um talhão para abrir os detalhes</small>
          </div>
          <div className="prop-op-plots-grid">
            {talhoes.map(talhao => {
              const status = STATUS[talhao.operationalStatus] || STATUS.stable;
              const StatusIcon = status.icon;
              return (
                <button
                  type="button"
                  key={talhao.id}
                  className={`prop-op-plot-card status-${talhao.operationalStatus}`}
                  onClick={() => setTalhaoSelecionado(talhao)}
                  aria-label={`Abrir acompanhamento do ${talhao.nome}`}
                >
                  <div className="prop-op-plot-head">
                    <div><span><FaLeaf aria-hidden="true" /></span><div><strong>{talhao.nome}</strong><small>{talhao.cultura} · {formatArea(talhao.area)} ha</small></div></div>
                    <em><StatusIcon aria-hidden="true" /> {status.label}</em>
                  </div>
                  <div className="prop-op-plot-stage"><span><i style={{ width: `${talhao.cycleProgress}%` }} /></span><small>{talhao.stage} · {talhao.age}</small></div>
                  <div className="prop-op-plot-metrics">
                    <span><small>Água no solo</small><strong>{talhao.waterPct}%</strong></span>
                    <span><small>NDVI</small><strong>{Number(talhao.ndvi).toFixed(2)}</strong></span>
                    <span><small>Leitura</small><strong>{talhao.lastReading}</strong></span>
                  </div>
                  <div className="prop-op-plot-action"><span>{talhao.nextAction}</span><FaArrowRight aria-hidden="true" /></div>
                </button>
              );
            })}
          </div>
        </section>
      </main>
    </div>
  );
}
